import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AttachmentsService } from '@kinet-open-network/client/attachments/data-access';
import { ABILITY } from '@kinet-open-network/client/auth/data-access';
import { API_URL } from '@kinet-open-network/client/core/api-client';
import {
  PredefinedText,
  PredefinedTextsService,
} from '@kinet-open-network/client/predefined-texts/data-access';
import {
  QuillEditorContentComponent,
  confirmDialog,
  toast,
} from '@kinet-open-network/client/shared/ui';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Editor, EditorModule } from 'primeng/editor';
import { firstValueFrom } from 'rxjs';

const ATTACHMENT_LOADING_DATA_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='120' height='80' viewBox='0 0 120 80'><rect width='120' height='80' fill='#f3f4f6'/><circle cx='60' cy='40' r='12' fill='none' stroke='#9ca3af' stroke-width='4' stroke-linecap='round' stroke-dasharray='52 24'><animateTransform attributeName='transform' type='rotate' from='0 60 40' to='360 60 40' dur='1s' repeatCount='indefinite'/></circle></svg>",
)}`;
const ATTACHMENT_EMBED_TAG = 'kon-attachment-image';
const ATTACHMENT_EMBED_BLOT = 'attachment-image';
const ATTACHMENT_UPLOADING_PREFIX = 'uploading-';

interface AttachmentPreviewElement extends HTMLElement {
  setPreviewSource: (src: string) => void;
  setCanonicalSource: (src: string) => void;
  setLoadingSource: () => void;
}

const extractAttachmentIdFromUrl = (src: string): string | null =>
  src.match(/\/attachments\/([0-9a-f-]{36})/i)?.[1] ?? null;

const ensureAttachmentPreviewElementRegistered = () => {
  if (customElements.get(ATTACHMENT_EMBED_TAG)) {
    return;
  }

  class KonAttachmentImageElement extends HTMLElement {
    private imageElement: HTMLImageElement | null = null;

    connectedCallback() {
      this.ensureShadowView();
      if (!this.imageElement?.getAttribute('src')) {
        this.setLoadingSource();
      }
    }

    setPreviewSource(src: string) {
      this.ensureShadowView();
      this.imageElement?.setAttribute('src', src);
    }

    setCanonicalSource(src: string) {
      this.ensureShadowView();
      this.imageElement?.setAttribute('src', src);
    }

    setLoadingSource() {
      this.ensureShadowView();
      this.imageElement?.setAttribute('src', ATTACHMENT_LOADING_DATA_URL);
    }

    private ensureShadowView() {
      if (this.shadowRoot) {
        this.imageElement = this.shadowRoot.querySelector('img');
        return;
      }

      const shadowRoot = this.attachShadow({ mode: 'open' });
      const style = document.createElement('style');
      style.textContent = `
        :host {
          display: block;
          min-height: 2.5rem;
        }

        img {
          display: block;
          max-width: 100%;
          height: auto;
        }
      `;

      const image = document.createElement('img');
      image.setAttribute('alt', 'attachment image');

      shadowRoot.append(style, image);
      this.imageElement = image;
    }
  }

  customElements.define(ATTACHMENT_EMBED_TAG, KonAttachmentImageElement);
};

interface EditorProps extends FormlyFieldProps {
  style?: Record<string, unknown>;
  hiddenItems?: string[];
  predefinedTextKey?: string;
}

export interface FormlyEditorFieldConfig
  extends FormlyFieldConfig<EditorProps> {}

@Component({
  selector: 'kon-editor-type',
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    FormlySelectModule,
    ReactiveFormsModule,
    EditorModule,
    DialogModule,
    ButtonModule,
    QuillEditorContentComponent,
  ],
  template: `
    <legend *ngIf="props.label">{{ props.label }}</legend>
    <p-editor
      #editor
      [formControl]="formControl"
      [formlyAttributes]="field"
      [style]="props.style"
      [readonly]="disabled"
      [formats]="formats"
      (onInit)="onEditorInit()"
    ></p-editor>

    <p-dialog
      header="Preddefinované texty"
      [(visible)]="predefinedTextDialogVisible"
      [modal]="true"
      [style]="{ width: '40rem' }"
    >
      @if (loadingPredefinedTexts()) {
      <p>Načítavam...</p>
      } @else if (!savedPredefinedTexts().length) {
      <p>Žiadne uložené texty pre toto pole.</p>
      } @else {
      <ul class="flex flex-col gap-2 max-h-96 overflow-y-auto">
        @for (predefinedText of savedPredefinedTexts(); track predefinedText.id)
        {
        <li class="flex items-stretch gap-2">
          @if (editingPredefinedTextId() === predefinedText.id) {
          <div class="flex-1 flex flex-col gap-2">
            <p-editor
              [formControl]="editingTextControl"
              [style]="{ height: '160px' }"
            ></p-editor>
            <div class="flex justify-end gap-2">
              <p-button
                label="Zrušiť"
                severity="secondary"
                (onClick)="cancelEditPredefinedText()"
              ></p-button>
              <p-button
                label="Uložiť"
                (onClick)="saveEditedPredefinedText(predefinedText)"
              ></p-button>
            </div>
          </div>
          } @else {
          <button
            type="button"
            class="flex-1 text-left border rounded p-2 hover:bg-gray-50"
            (click)="insertPredefinedText(predefinedText)"
          >
            <kon-quill-editor-content
              [content]="predefinedText.text"
            ></kon-quill-editor-content>
          </button>
          @if (canUpdatePredefinedText()) {
          <button
            type="button"
            class="pi pi-pencil text-gray-500 px-2"
            title="Upraviť"
            (click)="startEditPredefinedText(predefinedText)"
          ></button>
          } @if (canDeletePredefinedText()) {
          <button
            type="button"
            class="pi pi-trash text-red-500 px-2"
            title="Odstrániť"
            (click)="deletePredefinedText(predefinedText)"
          ></button>
          } }
        </li>
        }
      </ul>
      } @if (canSavePredefinedText()) {
      <div class="mt-4 flex justify-end">
        <p-button
          label="Uložiť aktuálny text"
          (onClick)="saveCurrentPredefinedText()"
        ></p-button>
      </div>
      }
    </p-dialog>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyEditorTypeComponent
  extends FieldType<FieldTypeConfig<EditorProps>>
  implements AfterViewInit, OnDestroy
{
  @ViewChild('editor') editor!: Editor;
  private destroyRef = inject(DestroyRef);
  private attachmentsService = inject(AttachmentsService);
  private apiUrl = inject(API_URL);
  private predefinedTextsService = inject(PredefinedTextsService);
  private ability = inject(ABILITY);
  private attachmentPreviewMap = new Map<string, string>();
  private loadingAttachmentIds = new Set<string>();
  private failedAttachmentIds = new Set<string>();
  private trackedAttachmentIds = new Set<string>();
  private pendingAttachmentDeletions = new Set<string>();
  private hasAttachmentSnapshot = false;
  private registeredTextChangeListener = false;
  private registeredAttachmentEmbed = false;
  private registeredPredefinedTextButton = false;
  private initTimeoutId: ReturnType<typeof setTimeout> | null = null;

  predefinedTextDialogVisible = signal(false);
  savedPredefinedTexts = signal<PredefinedText[]>([]);
  loadingPredefinedTexts = signal(false);

  canSavePredefinedText = computed(() =>
    this.ability().can('create', 'PredefinedText'),
  );
  canUpdatePredefinedText = computed(() =>
    this.ability().can('update', 'PredefinedText'),
  );
  canDeletePredefinedText = computed(() =>
    this.ability().can('delete', 'PredefinedText'),
  );
  editingPredefinedTextId = signal<string | null>(null);
  editingTextControl = new FormControl<string>('', { nonNullable: true });

  get disabled() {
    return !!this.props.disabled;
  }

  override defaultOptions?: Partial<FieldTypeConfig<EditorProps>> | undefined =
    {
      props: {
        style: {
          height: '320px',
        },
      },
    };

  get hiddenItems() {
    return this.props.hiddenItems || ['image'];
  }

  get formats() {
    const baseFormats = [
      'background',
      'bold',
      'color',
      'font',
      'code',
      'italic',
      'link',
      'size',
      'strike',
      'script',
      'underline',
      'blockquote',
      'header',
      'indent',
      'list',
      'align',
      'direction',
      'code-block',
      'formula',
      'image',
    ].filter((format) => !this.hiddenItems.includes(format));

    if (!baseFormats.includes(ATTACHMENT_EMBED_BLOT)) {
      baseFormats.push(ATTACHMENT_EMBED_BLOT);
    }

    return baseFormats;
  }

  hideToolbarItems() {
    const quill = this.editor?.quill;
    if (!quill) {
      return;
    }

    const buttons = (quill.theme.modules?.toolbar?.controls
      ?.filter((ctrl: [string, any]) => this.hiddenItems.includes(ctrl[0]))
      ?.map((ctrl: [string, any]) => ctrl[1]) ?? []) as HTMLButtonElement[];

    buttons.forEach((button) => (button.style.display = 'none'));
  }

  onEditorInit() {
    this.normalizeAttachmentUrlsInContent();
    this.registerAttachmentEmbed();
    this.hideToolbarItems();
    this.registerImageHandler();
    this.registerPredefinedTextButton();
    this.registerAttachmentPreviewLoader();
    this.transformCanonicalImagesToEmbeds();
    this.renderAttachmentPreviews();
    this.syncCanonicalContentToFormControl();
    this.refreshTrackedAttachmentIds();
  }

  private normalizeAttachmentUrlsInContent() {
    const value = this.formControl.value;

    if (typeof value !== 'string') {
      return;
    }

    let normalized = value.replace(
      /(["'])\/attachments\/([0-9a-f-]{36})\/download\1/gi,
      (match, quote, id) =>
        `${quote}${this.apiUrl}/attachments/${id}/download${quote}`,
    );

    normalized = this.ensureCanonicalAttachmentImageMetadata(normalized);

    if (normalized !== value) {
      this.formControl.setValue(normalized, {
        emitEvent: false,
        emitModelToViewChange: false,
      });
    }
  }

  private registerImageHandler() {
    if (this.hiddenItems.includes('image')) {
      return;
    }

    const quill = this.editor?.quill;
    const toolbar = quill?.getModule('toolbar');

    if (!toolbar) {
      return;
    }

    toolbar.addHandler('image', () => {
      void this.openImagePicker();
    });
  }

  private registerPredefinedTextButton() {
    if (
      this.registeredPredefinedTextButton ||
      this.hiddenItems.includes('predefined-text') ||
      !this.props.predefinedTextKey
    ) {
      return;
    }

    const quill = this.editor?.quill;
    const toolbarContainer = quill?.getModule('toolbar')?.container as
      | HTMLElement
      | undefined;

    if (!toolbarContainer) {
      return;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ql-predefined-text';
    button.title = 'Preddefinované texty';
    button.innerHTML = '<i class="pi pi-bookmark"></i>';
    button.addEventListener('click', () => {
      void this.openPredefinedTextDialog();
    });
    if (this.disabled) {
      button.setAttribute('disabled', 'true');
    }

    toolbarContainer.appendChild(button);
    this.registeredPredefinedTextButton = true;
  }

  async openPredefinedTextDialog() {
    this.predefinedTextDialogVisible.set(true);
    this.loadingPredefinedTexts.set(true);

    try {
      const texts = await firstValueFrom(
        this.predefinedTextsService.getByKey(
          this.props.predefinedTextKey ?? '',
        ),
      );
      this.savedPredefinedTexts.set(texts);
    } catch {
      toast({
        severity: 'danger',
        summary: 'Chyba',
        detail: 'Preddefinované texty sa nepodarilo načítať.',
      });
    } finally {
      this.loadingPredefinedTexts.set(false);
    }
  }

  insertPredefinedText(predefinedText: PredefinedText) {
    const quill = this.editor?.quill;
    if (!quill) {
      return;
    }

    const range = quill.getSelection(true);
    const index = range?.index ?? quill.getLength();
    quill.clipboard.dangerouslyPasteHTML(index, predefinedText.text, 'user');
    this.predefinedTextDialogVisible.set(false);
  }

  deletePredefinedText(predefinedText: PredefinedText) {
    if (!this.canDeletePredefinedText()) {
      return;
    }

    confirmDialog({
      header: 'Odstrániť text',
      message: 'Naozaj chcete odstrániť tento preddefinovaný text?',
      accept: async () => {
        try {
          await firstValueFrom(
            this.predefinedTextsService.delete(predefinedText.id),
          );
          this.savedPredefinedTexts.update((texts) =>
            texts.filter((text) => text.id !== predefinedText.id),
          );
          toast({
            severity: 'success',
            summary: 'Odstránené',
            detail: 'Text bol odstránený.',
          });
        } catch {
          toast({
            severity: 'danger',
            summary: 'Chyba',
            detail: 'Text sa nepodarilo odstrániť.',
          });
        }
      },
    });
  }

  startEditPredefinedText(predefinedText: PredefinedText) {
    if (!this.canUpdatePredefinedText()) {
      return;
    }

    this.editingPredefinedTextId.set(predefinedText.id);
    this.editingTextControl.setValue(predefinedText.text);
  }

  cancelEditPredefinedText() {
    this.editingPredefinedTextId.set(null);
    this.editingTextControl.setValue('');
  }

  async saveEditedPredefinedText(predefinedText: PredefinedText) {
    if (!this.canUpdatePredefinedText()) {
      return;
    }

    const text = this.editingTextControl.value.trim();

    if (!text) {
      toast({
        severity: 'warning',
        summary: 'Upozornenie',
        detail: 'Text nemôže byť prázdny.',
      });
      return;
    }

    try {
      const updated = await firstValueFrom(
        this.predefinedTextsService.update(predefinedText.id, { text }),
      );
      this.savedPredefinedTexts.update((texts) =>
        texts.map((item) => (item.id === predefinedText.id ? updated : item)),
      );
      this.cancelEditPredefinedText();
      toast({
        severity: 'success',
        summary: 'Uložené',
        detail: 'Text bol upravený.',
      });
    } catch {
      toast({
        severity: 'danger',
        summary: 'Chyba',
        detail: 'Text sa nepodarilo upraviť.',
      });
    }
  }

  async saveCurrentPredefinedText() {
    if (!this.canSavePredefinedText()) {
      return;
    }

    const text =
      typeof this.formControl.value === 'string' ? this.formControl.value : '';

    if (!text.trim()) {
      toast({
        severity: 'warning',
        summary: 'Upozornenie',
        detail: 'Nie je čo uložiť.',
      });
      return;
    }

    try {
      const saved = await firstValueFrom(
        this.predefinedTextsService.create({
          key: this.props.predefinedTextKey ?? '',
          text,
        }),
      );
      this.savedPredefinedTexts.update((texts) => [saved, ...texts]);
      toast({
        severity: 'success',
        summary: 'Uložené',
        detail: 'Text bol uložený.',
      });
    } catch {
      toast({
        severity: 'danger',
        summary: 'Chyba',
        detail: 'Text sa nepodarilo uložiť.',
      });
    }
  }

  private registerAttachmentEmbed() {
    if (this.registeredAttachmentEmbed || !this.editor?.quill) {
      return;
    }

    ensureAttachmentPreviewElementRegistered();

    const quill = this.editor.quill;
    const QuillCtor = quill.constructor as any;
    const EmbedBlot = QuillCtor.import('blots/embed');

    class AttachmentImageBlot extends EmbedBlot {
      static blotName = ATTACHMENT_EMBED_BLOT;
      static tagName = ATTACHMENT_EMBED_TAG;

      static create(
        value: { id?: string; url?: string; uploading?: boolean } | string,
      ) {
        const node = super.create() as HTMLElement;
        const url = typeof value === 'string' ? value : value.url || '';
        const id =
          (typeof value === 'string' ? null : value.id) ||
          extractAttachmentIdFromUrl(url) ||
          '';
        const uploading = typeof value === 'string' ? false : !!value.uploading;

        if (id) {
          node.setAttribute('data-attachment-id', id);
        }

        if (url) {
          node.setAttribute('data-attachment-url', url);
        }

        if (uploading) {
          node.setAttribute('data-uploading', 'true');
        }

        return node;
      }

      static value(node: HTMLElement) {
        return {
          id: node.getAttribute('data-attachment-id') || '',
          url: node.getAttribute('data-attachment-url') || '',
          uploading: node.getAttribute('data-uploading') === 'true',
        };
      }
    }

    QuillCtor.register(AttachmentImageBlot, true);
    this.registeredAttachmentEmbed = true;
  }

  private registerAttachmentPreviewLoader() {
    if (this.registeredTextChangeListener || !this.editor?.quill) {
      return;
    }

    const quill = this.editor.quill;
    const textChangeHandler = (
      _delta: unknown,
      _oldDelta: unknown,
      source: string,
    ) => {
      this.renderAttachmentPreviews();
      this.syncCanonicalContentToFormControl();

      if (source === 'user') {
        // this.deleteRemovedAttachmentsFromContent();
      } else {
        this.refreshTrackedAttachmentIds();
      }
    };

    const selectionChangeHandler = (range: unknown) => {
      if (range == null) {
        this.syncCanonicalContentToFormControl();
      }
    };

    quill.on('text-change', textChangeHandler);
    quill.on('selection-change', selectionChangeHandler);

    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.normalizeAttachmentUrlsInContent();
        queueMicrotask(() => {
          this.transformCanonicalImagesToEmbeds();
          this.renderAttachmentPreviews();
          this.syncCanonicalContentToFormControl();
          this.refreshTrackedAttachmentIds();
        });
      });

    this.destroyRef.onDestroy(() => {
      quill.off('text-change', textChangeHandler);
      quill.off('selection-change', selectionChangeHandler);
      this.cleanupAllPreviewUrls();
    });

    this.registeredTextChangeListener = true;
  }

  private renderAttachmentPreviews() {
    const quill = this.editor?.quill;

    if (!quill) {
      return;
    }

    const attachmentNodes = Array.from(
      quill.root.querySelectorAll(ATTACHMENT_EMBED_TAG),
    ) as HTMLElement[];
    const activeAttachmentIds = new Set<string>();

    attachmentNodes.forEach((node) => {
      const id = node.getAttribute('data-attachment-id') || '';
      const canonicalUrl = node.getAttribute('data-attachment-url') || '';
      const isUploading = node.getAttribute('data-uploading') === 'true';

      if (isUploading) {
        (node as AttachmentPreviewElement).setLoadingSource();
        return;
      }

      if (!id || !canonicalUrl) {
        return;
      }

      activeAttachmentIds.add(id);

      const previewUrl = this.attachmentPreviewMap.get(id);
      if (previewUrl) {
        (node as AttachmentPreviewElement).setPreviewSource(previewUrl);
        return;
      }

      if (this.failedAttachmentIds.has(id)) {
        (node as AttachmentPreviewElement).setCanonicalSource(canonicalUrl);
        return;
      }

      (node as AttachmentPreviewElement).setLoadingSource();
      this.loadAttachmentPreview(id);
    });

    this.pruneAttachmentPreviewCache(activeAttachmentIds);
  }

  private transformCanonicalImagesToEmbeds() {
    const quill = this.editor?.quill;
    if (!quill) {
      return;
    }

    const images = Array.from(
      quill.root.querySelectorAll('img[src]'),
    ) as HTMLImageElement[];

    if (images.length === 0) {
      return;
    }

    let changed = false;

    // Preserve current selection before DOM manipulation
    const selection = quill.getSelection(true);

    images.forEach((image) => {
      const src = image.getAttribute('src') || '';
      const id =
        image.getAttribute('data-attachment-id') ||
        this.extractAttachmentId(src);

      if (!id) {
        return;
      }

      const canonicalUrl = this.buildCanonicalAttachmentUrl(id, src);
      const embedNode = document.createElement(ATTACHMENT_EMBED_TAG);
      embedNode.setAttribute('data-attachment-id', id);
      embedNode.setAttribute('data-attachment-url', canonicalUrl);
      image.replaceWith(embedNode);
      changed = true;
    });

    if (changed) {
      quill.update('silent');
      // Restore selection after DOM manipulation
      if (selection) {
        queueMicrotask(() => {
          quill.setSelection(selection.index, selection.length, 'silent');
        });
      }
    }
  }

  private loadAttachmentPreview(id: string) {
    if (
      this.loadingAttachmentIds.has(id) ||
      this.attachmentPreviewMap.has(id) ||
      this.failedAttachmentIds.has(id)
    ) {
      return;
    }

    this.loadingAttachmentIds.add(id);

    this.attachmentsService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (attachment) => {
          try {
            const previewUrl =
              attachment.dataUrl ||
              (attachment.data
                ? `data:${attachment.mimeType};base64,${attachment.data}`
                : null);

            if (!previewUrl) {
              throw new Error('Attachment has no previewable content');
            }

            this.attachmentPreviewMap.set(id, previewUrl);
          } catch {
            this.failedAttachmentIds.add(id);
          }
          this.loadingAttachmentIds.delete(id);
          this.renderAttachmentPreviews();
          this.syncCanonicalContentToFormControl();
        },
        error: () => {
          this.loadingAttachmentIds.delete(id);
          this.failedAttachmentIds.add(id);
          this.renderAttachmentPreviews();
        },
      });
  }

  private syncCanonicalContentToFormControl() {
    const quill = this.editor?.quill;
    if (!quill) {
      return;
    }

    const canonicalHtml = this.buildCanonicalHtml(quill.root.innerHTML);
    const currentValue =
      typeof this.formControl.value === 'string' ? this.formControl.value : '';

    if (canonicalHtml !== currentValue) {
      this.formControl.setValue(canonicalHtml, {
        emitEvent: false,
        emitModelToViewChange: false,
      });
    }
  }

  private refreshTrackedAttachmentIds() {
    const html =
      typeof this.formControl.value === 'string' ? this.formControl.value : '';
    const currentIds = this.extractAttachmentIdsFromHtml(html);

    this.trackedAttachmentIds = currentIds;
    this.hasAttachmentSnapshot = true;
  }

  private deleteRemovedAttachmentsFromContent() {
    const html =
      typeof this.formControl.value === 'string' ? this.formControl.value : '';
    const currentIds = this.extractAttachmentIdsFromHtml(html);

    if (!this.hasAttachmentSnapshot) {
      this.trackedAttachmentIds = currentIds;
      this.hasAttachmentSnapshot = true;
      return;
    }

    const removedIds = [...this.trackedAttachmentIds].filter(
      (id) => !currentIds.has(id),
    );

    removedIds.forEach((id) => {
      if (
        !id ||
        id.startsWith(ATTACHMENT_UPLOADING_PREFIX) ||
        this.pendingAttachmentDeletions.has(id)
      ) {
        return;
      }

      this.pendingAttachmentDeletions.add(id);
      this.attachmentsService
        .delete(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.pendingAttachmentDeletions.delete(id);
          },
          error: () => {
            this.pendingAttachmentDeletions.delete(id);
          },
        });
    });

    this.trackedAttachmentIds = currentIds;
  }

  private extractAttachmentIdsFromHtml(html: string): Set<string> {
    if (!html) {
      return new Set<string>();
    }

    const host = document.createElement('div');
    host.innerHTML = html;
    const ids = new Set<string>();

    host.querySelectorAll<HTMLElement>(ATTACHMENT_EMBED_TAG).forEach((node) => {
      const id = node.getAttribute('data-attachment-id');
      if (id && !id.startsWith(ATTACHMENT_UPLOADING_PREFIX)) {
        ids.add(id);
      }
    });

    host.querySelectorAll<HTMLImageElement>('img[src]').forEach((img) => {
      const id =
        img.getAttribute('data-attachment-id') ||
        this.extractAttachmentId(img.getAttribute('src') || '');

      if (id && !id.startsWith(ATTACHMENT_UPLOADING_PREFIX)) {
        ids.add(id);
      }
    });

    return ids;
  }

  private buildCanonicalHtml(html: string): string {
    const host = document.createElement('div');
    host.innerHTML = html;

    const attachmentEmbeds =
      host.querySelectorAll<HTMLElement>(ATTACHMENT_EMBED_TAG);

    attachmentEmbeds.forEach((embed) => {
      if (embed.getAttribute('data-uploading') === 'true') {
        embed.remove();
        return;
      }

      const id = embed.getAttribute('data-attachment-id');
      const url = embed.getAttribute('data-attachment-url') || '';

      if (!id) {
        embed.remove();
        return;
      }

      const canonicalUrl = this.buildCanonicalAttachmentUrl(id, url);
      const image = document.createElement('img');
      image.setAttribute('src', canonicalUrl);
      image.setAttribute('data-attachment-id', id);
      embed.replaceWith(image);
    });

    const images = host.querySelectorAll<HTMLImageElement>('img[src]');
    images.forEach((img) => {
      const src = img.getAttribute('src') || '';
      const id =
        img.getAttribute('data-attachment-id') || this.extractAttachmentId(src);

      if (!id) {
        return;
      }

      const canonicalUrl = `${this.apiUrl}/attachments/${id}/download`;
      img.setAttribute('src', canonicalUrl);
      img.setAttribute('data-attachment-id', id);
    });

    return host.innerHTML;
  }

  private ensureCanonicalAttachmentImageMetadata(html: string): string {
    const host = document.createElement('div');
    host.innerHTML = html;
    let changed = false;

    const images = host.querySelectorAll<HTMLImageElement>('img[src]');
    images.forEach((img) => {
      const src = img.getAttribute('src') || '';
      const id =
        img.getAttribute('data-attachment-id') || this.extractAttachmentId(src);

      if (!id) {
        return;
      }

      const canonicalUrl = this.buildCanonicalAttachmentUrl(id, src);

      if (img.getAttribute('src') !== canonicalUrl) {
        img.setAttribute('src', canonicalUrl);
        changed = true;
      }

      if (img.getAttribute('data-attachment-id') !== id) {
        img.setAttribute('data-attachment-id', id);
        changed = true;
      }
    });

    return changed ? host.innerHTML : html;
  }

  private pruneAttachmentPreviewCache(activeAttachmentIds: Set<string>) {
    for (const [id] of this.attachmentPreviewMap.entries()) {
      if (activeAttachmentIds.has(id)) {
        continue;
      }

      this.attachmentPreviewMap.delete(id);
      this.failedAttachmentIds.delete(id);
      this.loadingAttachmentIds.delete(id);
    }

    for (const id of this.failedAttachmentIds) {
      if (!activeAttachmentIds.has(id)) {
        this.failedAttachmentIds.delete(id);
      }
    }
  }

  private cleanupAllPreviewUrls() {
    this.attachmentPreviewMap.clear();
    this.loadingAttachmentIds.clear();
    this.failedAttachmentIds.clear();
  }

  private buildCanonicalAttachmentUrl(id: string, currentSrc: string): string {
    const currentMatch = currentSrc.match(/^https?:\/\//i);
    if (currentMatch) {
      return currentSrc;
    }

    return `${this.apiUrl}/attachments/${id}/download`;
  }

  private extractAttachmentId(src: string): string | null {
    return src.match(/\/attachments\/([0-9a-f-]{36})/i)?.[1] ?? null;
  }

  private async openImagePicker() {
    // Save the current selection before file picker opens (it will steal focus)
    const quill = this.editor?.quill;
    const savedSelection = quill?.getSelection(true);

    if (quill && savedSelection) {
      quill.setSelection(savedSelection.index, savedSelection.length, 'silent');
    }

    if (this.disabled) {
      return;
    }

    const imageInput = document.createElement('input');
    imageInput.type = 'file';
    imageInput.accept = 'image/*';
    imageInput.addEventListener(
      'change',
      () => {
        const file = imageInput.files?.[0];
        if (file) {
          // Restore selection before uploading
          if (quill && savedSelection) {
            quill.setSelection(
              savedSelection.index,
              savedSelection.length,
              'silent',
            );
          }
          void this.uploadAndInsertImage(file);
        }
      },
      { once: true },
    );

    imageInput.click();
  }

  private async uploadAndInsertImage(file: File) {
    const quill = this.editor?.quill;
    if (!quill) {
      return;
    }

    const range = quill.getSelection(true);
    const index = range?.index ?? quill.getLength();
    const temporaryAttachmentId = `${ATTACHMENT_UPLOADING_PREFIX}${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    quill.insertEmbed(
      index,
      ATTACHMENT_EMBED_BLOT,
      { id: temporaryAttachmentId, uploading: true },
      'user',
    );
    quill.setSelection(index + 1, 0, 'user');
    this.renderAttachmentPreviews();

    try {
      const dataUrl = await this.readFileAsDataUrl(file);
      const attachment = await firstValueFrom(
        this.attachmentsService.create({
          data: dataUrl,
          name: file.name,
        }),
      );

      const imageUrl = `${this.apiUrl}/attachments/${attachment.id}/download`;
      const temporaryNode = quill.root.querySelector(
        `${ATTACHMENT_EMBED_TAG}[data-attachment-id="${temporaryAttachmentId}"]`,
      ) as HTMLElement | null;

      if (temporaryNode) {
        temporaryNode.setAttribute('data-attachment-id', attachment.id);
        temporaryNode.setAttribute('data-attachment-url', imageUrl);
        temporaryNode.removeAttribute('data-uploading');
      }

      this.attachmentPreviewMap.set(attachment.id, dataUrl);
      this.formControl.markAsDirty();
      this.renderAttachmentPreviews();
      this.syncCanonicalContentToFormControl();
    } catch {
      const temporaryNode = quill.root.querySelector(
        `${ATTACHMENT_EMBED_TAG}[data-attachment-id="${temporaryAttachmentId}"]`,
      ) as HTMLElement | null;

      if (temporaryNode) {
        temporaryNode.remove();
        quill.update('silent');
      }

      toast({
        severity: 'danger',
        summary: 'Chyba',
        detail: 'Obrázok sa nepodarilo nahrať.',
      });
    }
  }

  private readFileAsDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (e) => {
        reject(e);
      };

      reader.readAsDataURL(file);
    });
  }

  ngAfterViewInit(): void {
    // TODO: Fix this hack
    // Neviem prečo ale editor sa neinicializuje spravne na ticket-messages.component.
    // Podla zdrojakov to vyzera, že v momente, ked sa inicializuje ešte nie je connectnuty na DOM.
    // https://github.com/primefaces/primeng/blob/master/src/app/components/editor/editor.ts#L200
    this.initTimeoutId = setTimeout(() => {
      if (!this.editor) {
        return;
      }

      const editor = this.editor as any;
      if (editor?.isAttachedQuillEditorToDOM && !this.editor.quill) {
        editor.initQuillEditor();
      }

      if (!this.editor?.quill) {
        return;
      }

      this.normalizeAttachmentUrlsInContent();
      this.registerAttachmentEmbed();
      this.hideToolbarItems();
      this.registerImageHandler();
      this.registerPredefinedTextButton();
      this.registerAttachmentPreviewLoader();
      this.transformCanonicalImagesToEmbeds();
      this.renderAttachmentPreviews();
      this.syncCanonicalContentToFormControl();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.initTimeoutId !== null) {
      clearTimeout(this.initTimeoutId);
      this.initTimeoutId = null;
    }
  }
}
